import React, { useMemo } from "react";
import { Typography } from "antd";
import { SbTag, SbTooltip } from "uiCore";
import { getFilters } from "../RedeploymentCenterV2/RedeploymentCenterV2.utils";
import { JOB_OPENING_STATUS, ROLE_STATUS_OPEN } from "../talentMobility.constants";
import { PRIORITY_TAG_COLOR } from "./TalentMobilityManagerV2.constants";
import { RoleTitleLinkStyled } from "./TalentMobilityManagerV2.styles";

const { Text } = Typography;

// Open/Discoverable are per-role roll-ups now (how many of the role's
// employees are individually Open/Discoverable) — same success-tag-vs-muted-
// text convention as the Matches column, with the role's headcount alongside
// so "3" reads as "3 of how many".
const renderCount = (count, total) =>
  count > 0 ? (
    <SbTag color="success" style={{ fontSize: 13 }}>
      {count}/{total}
    </SbTag>
  ) : (
    <Text type="secondary" style={{ fontSize: 13 }}>
      0/{total}
    </Text>
  );

// Role Detail's URL carries every occupant it should show — every employee
// whose position is Open for this role (what a manager actually cares about:
// who's currently backfilling), or the whole roster if none are Open (a
// closed role's Title still has to go somewhere). Each occupant's own
// Open/Discoverable snapshot travels alongside their id — Role Detail passes
// the whole thing on to the Candidate Profile page's Shortlist action (as
// router state), which sends it to POST /talent-mobility/shortlist's
// `user_ids` so shortlisting a candidate also captures this role's occupants'
// job-status without a separate checkbox toggle. Field names already match
// PUT /job-opening-status's own body (job_opening_status / is_job_discoverable)
// so the payload can be forwarded through untouched.
const openOccupants = (role) => {
  const openEmployees = role.employees.filter((employee) => employee.status === ROLE_STATUS_OPEN);
  return (openEmployees.length ? openEmployees : role.employees).map((employee) => ({
    user_id: employee.id,
    job_opening_status: employee.status === ROLE_STATUS_OPEN ? JOB_OPENING_STATUS.OPEN : JOB_OPENING_STATUS.CLOSED,
    is_job_discoverable: Boolean(employee.discoverable),
  }));
};

const useTalentMobilityManagerColumns = ({ roles, thresholds, navigate }) =>
  useMemo(
    () => [
      {
        title: "Requisition Number",
        dataIndex: "requisitionNumber",
        filters: getFilters(roles, "requisitionNumber"),
        filterSearch: true,
        onFilter: (value, record) => record.requisitionNumber === value,
        render: (v) => v || "—",
      },
      {
        // AntD appends the filter funnel icon right after whatever this
        // `title` renders, as a flex sibling of it inside ONE flex row (see
        // sharedTable.styles.js's flex-start override) — any two-line JSX
        // here (a "Title" line plus the caption) makes that row taller than
        // one line, and every attempt to then re-center the icon against
        // "Title" specifically (rather than the row's full height) fought
        // the same cross-axis math every other column relies on unmodified.
        // Keeping `title` a plain string sidesteps that entirely: the icon
        // aligns exactly like it does on every other column. The caption
        // itself is rendered as a ::after on this header cell instead (see
        // sharedTable.styles.js's .tm-title-col-header) -- a real sibling
        // AFTER the filter row, not inside it, so it adds a second line
        // below without the row itself ever growing.
        title: "Title",
        dataIndex: "title",
        filters: getFilters(roles, "title"),
        filterSearch: true,
        onFilter: (value, record) => record.title === value,
        onHeaderCell: () => ({ className: "tm-title-col-header" }),
        // Every Open employee's id, plus this row's own JD metadata, travels
        // as router state, not the URL (RoleDetailV2 reads location.state —
        // no separate people-profile-details fetch needed for a role this
        // page already has client-side) — same pattern RedeploymentCenterV2
        // uses for loadPlanId, keeps the URL to just the role. stopPropagation
        // so clicking the title doesn't also toggle this row's accordion
        // expand (see TalentMobilityManagerV2's onRow).
        render: (title, record) => (
          <RoleTitleLinkStyled
            onClick={(e) => {
              e.stopPropagation();
              const occupants = openOccupants(record);
              navigate(`/talent-mobility/role/${record.jobId}`, {
                state: {
                  occupantIds: occupants.map((occupant) => occupant.user_id),
                  openOccupants: occupants,
                  role: {
                    title: record.title,
                    function: record.function,
                    subFunction: record.subFunction,
                    location: record.location,
                    level: record.level,
                  },
                },
              });
            }}
          >
            {title}
          </RoleTitleLinkStyled>
        ),
      },
      {
        title: "Business Unit",
        dataIndex: "businessUnit",
        filters: getFilters(roles, "businessUnit"),
        filterSearch: true,
        onFilter: (value, record) => record.businessUnit === value,
      },
      {
        title: "Function",
        dataIndex: "function",
        filters: getFilters(roles, "function"),
        filterSearch: true,
        onFilter: (value, record) => record.function === value,
      },
      {
        title: "Sub-function",
        dataIndex: "subFunction",
        filters: getFilters(roles, "subFunction"),
        filterSearch: true,
        onFilter: (value, record) => record.subFunction === value,
      },
      {
        title: "Location",
        dataIndex: "location",
        filters: getFilters(roles, "location"),
        filterSearch: true,
        onFilter: (value, record) => record.location === value,
      },
      {
        title: "Level",
        dataIndex: "level",
        filters: getFilters(roles, "level"),
        filterSearch: true,
        onFilter: (value, record) => record.level === value,
      },
      {
        title: "Priority",
        dataIndex: "priority",
        align: "center",
        filters: getFilters(roles, "priority"),
        filterSearch: true,
        onFilter: (value, record) => record.priority === value,
        // align: "center" only centers the body cells — a filterable
        // header's title+funnel-icon group needs this class too.
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (priority) => (
          <SbTooltip content="Priority is read-only here — sourced from Jobs Library.">
            <SbTag color={PRIORITY_TAG_COLOR[priority] || "default"} style={{ fontSize: 13 }}>
              {priority}
            </SbTag>
          </SbTooltip>
        ),
      },
      {
        title: "Open",
        dataIndex: "openCount",
        align: "center",
        sorter: (a, b) => a.openCount - b.openCount,
        // align: "center" only centers the body cells — antd lays out a
        // sortable header's title + sort icon in their own flex box
        // untouched by `align`, so it needs this class (see
        // ClickableTableWrapperStyled's .tm-col-header-center rule) to
        // actually center as a group too.
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (count, record) => renderCount(count, record.employeeCount),
      },
      {
        title: "Discoverable",
        dataIndex: "discoverableCount",
        align: "center",
        sorter: (a, b) => a.discoverableCount - b.discoverableCount,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (count, record) => renderCount(count, record.employeeCount),
      },
      {
        title: `Matches (>${thresholds.green}%)`,
        dataIndex: "matchCount",
        align: "center",
        sorter: (a, b) => a.matchCount - b.matchCount,
        // Same reason Open/Discoverable above need it — align: "center" only
        // centers the body cells, not a sortable header's title+icon group.
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (count) =>
          count > 0 ? (
            <SbTag color="success" style={{ fontSize: 13 }}>
              {count}
            </SbTag>
          ) : (
            <Text type="secondary" style={{ fontSize: 13 }}>
              0
            </Text>
          ),
      },
    ],
    [roles, thresholds, navigate]
  );

export default useTalentMobilityManagerColumns;
